import { Button, createStyles, Grid, makeStyles, Typography } from "@material-ui/core";
import GeoAutoCompletion from "components/GeoAutoCompletion";
import { theme } from "config/theme";
import useText from "constants/text";
import { ITourStop, ITourTemplateAxe, NewRequestQuoteContext } from "contexts/RequestQuoteContext";
import { Address, EActionType, EVehiclesInput, TemplateStopsInput } from "generated/graphql";
import { CloseIcon } from "icons";
import React from "react";
import { TextField } from "ui/TextField";

interface Props {
  templateAxe?: ITourTemplateAxe;
  onVehicleButtonClicked: () => void;
  onAxeReccurenceButtonClicked: () => void;
  index: number;
}
const useStyles = makeStyles(() =>
  createStyles({
    button: {
      borderRadius: 2,
      border: "1px solid #399ebf ",
      backgroundColor: "#fff",
      color: "#399ebf",
      fontSize: 20,
      alignItems: "center",
      justifyContent: "center",
      display: "flex",
      width: 32,
      height: 32,
      cursor: "pointer",
    },
    buttonTitle: {
      fontWeight: "bold",
      fontSize: 12,
      marginLeft: "10px",
    },
    root: {
      display: "block",
      [theme.breakpoints.down("xs")]: {
        display: "block",
        margin: "auto",
        marginLeft: 0,
        width: "100%",
      },
    },
    vehicleButton: {
      fontSize: 13,
      width: "47%",
      background: "#e3f8fa",
      color: "#399ebf",
      fontFamily: "Montserrat",
      fontWeight: "bold",
      "&:hover": { background: "#e3f8fa" },
      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
        width: "130px",
      },
    },
    recurrenceButton: {
      width: "95%",
      fontSize: 13,
      background: "#e3f8fa",
      color: "#399ebf",
      fontFamily: "Montserrat",
      fontWeight: "bold",
      "&:hover": { background: "#e3f8fa" },
      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
        width: "130px",
      },
    },
    gridContainer: {
      display: "flex",
      marginLeft: "12px",
      height: "3rem",
      flexDirection: "row",
      marginTop: "10px",
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        marginLeft: "12px",
        height: "3rem",
        flexDirection: "row",
      },
    },
    gridStops: {
      display: "flex",
      flexDirection: "row",
      marginLeft: "12px",
      marginTop: "12px",
      marginBottom: "15px",
    },
    gridVehicle: {
      display: "contents",
    },
    geoAddress: {
      width: "24rem",
      marginBottom: "7px",
      [theme.breakpoints.down("xs")]: {
        width: "19rem",
        marginBottom: "7px",
      },
    },
    close: {
      float: "right",
      marginRight: "-50px",
      marginTop: "-30px",
      [theme.breakpoints.down("xs")]: {
        float: "right",
        marginRight: "-45px",
        marginTop: "-45px",
      },
    },
    gridAxe: {
      marginTop: 15,
    },
  })
);

const TourPoints = ({
  onVehicleButtonClicked,
  onAxeReccurenceButtonClicked,
  index: indexProps,
  templateAxe,
}: Props) => {
  const classes = useStyles();
  const { text } = useText();
  const { requestQuoteTour, setRequestQuoteTour } = React.useContext(NewRequestQuoteContext);

  const changeAxeInputName = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newState = { ...requestQuoteTour };
    newState.templateAxes[indexProps].name = e.target.value;
    setRequestQuoteTour({ ...newState });
  };
  const AllAxes = React.useMemo(() => {
    return requestQuoteTour?.templateAxes ? requestQuoteTour?.templateAxes : [];
  }, [requestQuoteTour?.templateAxes]);

  const currentTemplateAxe = React.useMemo(() => {
    return AllAxes.find((el) => el.uid === templateAxe?.uid);
  }, [requestQuoteTour, AllAxes, templateAxe]);

  const currentAxeIndex = React.useMemo(() => {
    return AllAxes.findIndex((_axe) => _axe.uid === templateAxe.uid);
  }, [AllAxes]);

  const handleAxeName = (event: any) => {
    const templateAxes = [...AllAxes];
    const newAxe: ITourTemplateAxe = {
      ...currentTemplateAxe,
      name: event.target.value,
    };
    templateAxes[currentAxeIndex] = newAxe;
    setRequestQuoteTour({ ...requestQuoteTour, templateAxes });
  };

  const currentAxeStops = React.useMemo(() => {
    return requestQuoteTour.templateAxes[indexProps]?.templateStops;
  }, [requestQuoteTour.templateAxes[indexProps]]);

  const currentAxe = React.useMemo(() => {
    return requestQuoteTour.templateAxes[indexProps];
  }, [requestQuoteTour.templateAxes[indexProps]]);

  const addNewStop = () => {
    if (currentAxeStops?.length && currentAxeStops.every((stop) => stop?.address?.address)) {
      const newStop: TemplateStopsInput = {
        address: null,
      };
      const newState = { ...requestQuoteTour };
      const axe = newState.templateAxes[indexProps];
      const stops = [...axe.templateStops, newStop];
      const newAxes = { ...axe, templateStops: [...stops] };
      newState.templateAxes[indexProps] = newAxes;

      setRequestQuoteTour({ ...newState });
    }
  };

  const handleSetStop = (stop: ITourStop, pos: number) => {
    const newState = requestQuoteTour;
    const axe = newState.templateAxes[indexProps];

    const stops = [...axe.templateStops];
    stops[pos] = stop;
    const newAxes = {
      ...axe,
      templateStops: stops,
    };
    newState.templateAxes[indexProps] = newAxes;

    setRequestQuoteTour({ ...newState });
  };

  const deleteStop = (index: number) => {
    const indexToDelete = index;
    const newState = { ...requestQuoteTour };
    const axe = newState.templateAxes[indexProps];

    let stops = [...axe.templateStops];
    stops = stops.filter((stop, i) => i !== indexToDelete);
    const newAxes = {
      ...axe,
      templateStops: stops,
    };
    newState.templateAxes[indexProps] = newAxes;

    setRequestQuoteTour({ ...newState });
  };

  const renderStops = React.useCallback(
    () =>
      currentAxeStops?.length > 2 &&
      currentAxeStops?.slice(2)?.map?.((stop, index) => {
        return (
          <Grid item xs={12} md={12}>
            <GeoAutoCompletion
              customLabel="Starting Point"
              address={stop?.address}
              onChange={(values: any) => handleSetStop({ ...stop, address: values }, index + 2)}
              required
            />
            <Button onClick={() => deleteStop(index + 2)} className={classes.close}>
              <CloseIcon />
            </Button>
          </Grid>
        );
      }),
    [requestQuoteTour]
  );

  const handleStartingPoint = (values: Address, pos: number) => {
    const point: ITourStop = {
      address: values,
      action: pos === 0 ? EActionType.Pickup : undefined,
    };
    handleSetStop(point, pos);
  };

  const handleVehicleType = () => {
    onVehicleButtonClicked();
  };

  const handleAxeReccurence = () => {
    onAxeReccurenceButtonClicked();
  };

  const numberOfVehicule = React.useMemo(() => {
    return currentAxe?.vehicles?.reduce((a: number, b: EVehiclesInput) => a + b.numberOfVehicule, 0);
  }, [requestQuoteTour]);

  return (
    <Grid container spacing={3} className={classes.root}>
      <Grid item xs={10} md={12}>
        <TextField label="Axe name" value={currentAxe?.name} name="name" onChange={handleAxeName} required fullWidth />
      </Grid>
      <Grid item xs={12} md={12}>
        <GeoAutoCompletion
          customLabel="Starting Point"
          address={currentAxeStops?.length && currentAxeStops[0]?.address}
          onChange={(values: Address) => {
            handleStartingPoint(values, 0);
          }}
          required
        />
      </Grid>
      <Grid item xs={12} md={12} className={classes.gridAxe}>
        <GeoAutoCompletion
          customLabel="Stop"
          address={currentAxeStops?.length && currentAxeStops[1]?.address}
          onChange={(values: Address) => {
            handleStartingPoint(values, 1);
          }}
          required
        />
      </Grid>
      {renderStops()}
      <Grid container alignItems="center" spacing={1} xs={7} md={6} className={classes.gridContainer}>
        <div onClick={addNewStop} className={classes.button}>
          +
        </div>
        <Typography color="primary" className={classes.buttonTitle}>
          {text.requestQuote.add_stop_button}
        </Typography>
      </Grid>
      <Grid item container alignItems="center" justifyContent="space-between" xs={12} md={12}>
        <Grid item xs={6}>
          <Button
            className={classes.recurrenceButton}
            onClick={handleAxeReccurence}
            color={currentAxe?.reccurenceType ? "primary" : "default"}
            variant={currentAxe?.reccurenceType ? "contained" : "outlined"}
          >
            {currentAxe?.reccurenceType
              ? `${currentAxe?.reccurenceType.replace(/_/g, " ")} `
              : text.requestQuote.define_reccurency}
          </Button>
        </Grid>
        <Grid item xs={6} className={classes.gridVehicle}>
          <Button
            className={classes.vehicleButton}
            onClick={handleVehicleType}
            color={numberOfVehicule ? "primary" : "default"}
            variant={numberOfVehicule ? "contained" : "outlined"}
          >
            {currentAxe?.vehicles?.length
              ? `${numberOfVehicule} ${text.requestQuote.vehicles_chosen}`
              : text.all.select_a_vehicle}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default TourPoints;
